import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { groupTripApi } from '../../api/groupTripApi';
import {
  bookingPresentation, caseImpactSummary, caseReference, createDisruptionActionKey, disruptionError,
  formatDisruptionTime, indexById, lifecycleLabel, normalizeDisruptionCase, REASON_OPTIONS, reasonLabel,
  travelerPresentation,
} from './disruptionModel';
import './disruption.css';

export default function DisruptionCasesPage() {
  const { groupTripId } = useParams();
  const [items, setItems] = useState([]);
  const [phase, setPhase] = useState('loading');
  const [error, setError] = useState('');
  const [showOpen, setShowOpen] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [travelers, setTravelers] = useState([]);
  const [form, setForm] = useState({ bookingIds: [], affectedTravelerIds: [], reason: 'OTHER', reasonDetail: '' });
  const [busy, setBusy] = useState(false);
  const openKey = useRef(null);
  const bookingIndex = useMemo(() => indexById(bookings, 'bookingId'), [bookings]);
  const travelerIndex = useMemo(() => indexById(travelers, 'travelerId'), [travelers]);

  const load = useCallback(async () => {
    setPhase('loading'); setError('');
    try {
      const payload = await groupTripApi.listDisruptionCases(groupTripId);
      setItems((Array.isArray(payload) ? payload : []).map(normalizeDisruptionCase));
      setPhase('ready');
    } catch (cause) {
      setError(disruptionError(cause).message); setPhase('error');
    }
  }, [groupTripId]);

  useEffect(() => { load(); }, [load]);

  async function beginOpen() {
    setShowOpen(true); setError('');
    try {
      const [bookingPayload, travelerPayload] = await Promise.all([
        groupTripApi.listBookings(groupTripId), groupTripApi.listTravelers(groupTripId),
      ]);
      setBookings(Array.isArray(bookingPayload?.items) ? bookingPayload.items : []);
      setTravelers(Array.isArray(travelerPayload?.items) ? travelerPayload.items : []);
    } catch (cause) {
      setError(disruptionError(cause).message);
    }
  }

  function toggle(field, id) {
    const value = String(id);
    setForm((current) => ({
      ...current,
      [field]: current[field].includes(value)
        ? current[field].filter((item) => item !== value) : [...current[field], value],
    }));
  }

  async function submitOpen(event) {
    event.preventDefault();
    if (form.bookingIds.length === 0 || busy) return;
    if (!openKey.current) openKey.current = createDisruptionActionKey();
    setBusy(true); setError('');
    try {
      await groupTripApi.openDisruptionCase(groupTripId, {
        bookingIds: form.bookingIds,
        affectedTravelerIds: form.affectedTravelerIds,
        reason: form.reason,
        reasonDetail: form.reasonDetail.trim() || null,
      }, openKey.current);
      openKey.current = null;
      setShowOpen(false);
      setForm({ bookingIds: [], affectedTravelerIds: [], reason: 'OTHER', reasonDetail: '' });
      await load();
    } catch (cause) {
      const failure = disruptionError(cause);
      setError(failure.message);
      if (failure.code === 95138) await load();
    } finally { setBusy(false); }
  }

  return (
    <main className="disruptions" aria-labelledby="disruption-list-title">
      <header className="disruptions__header">
        <div><p className="disruptions__eyebrow">揪團營運</p><h1 id="disruption-list-title">行程異動案件</h1></div>
        <div className="disruptions__actions">
          <Link to={`/group-trips/${groupTripId}`}>返回揪團</Link>
          <button type="button" onClick={beginOpen}>建立案件</button>
        </div>
      </header>
      {error ? <p className="disruptions__error" role="alert">{error}</p> : null}
      {phase === 'loading' ? <p className="disruptions__state">載入異動案件中…</p> : null}
      {phase === 'error' ? <div className="disruptions__state"><button type="button" onClick={load}>重新載入</button></div> : null}
      {phase === 'ready' && items.length === 0 ? <p className="disruptions__state">目前沒有異動案件。</p> : null}
      {phase === 'ready' && items.length ? (
        <ul className="disruptions__list">
          {items.map((item) => (
            <li key={item.caseId} className={`disruptions__case is-${item.lifecycle.toLowerCase()}`} data-lifecycle={item.lifecycle}>
              <Link to={`/group-trips/${groupTripId}/disruptions/${item.caseId}`}>
                <div className="disruptions__case-title"><strong>{item.reasonDetail || reasonLabel(item.reason)}</strong><span className="disruptions__badge">{lifecycleLabel(item.lifecycle)}</span></div>
                <p className="disruptions__secondary">{item.reasonDetail ? `${reasonLabel(item.reason)} · ` : ''}{caseReference(item.caseId)}</p>
                <p>{item.affectedTravelerIds.length} 位受影響旅伴 · {caseImpactSummary(item)}</p>
                <time className="disruptions__secondary">更新於 {formatDisruptionTime(item.updateTime || item.createTime)}</time>
              </Link>
            </li>
          ))}
        </ul>
      ) : null}
      {showOpen ? (
        <form className="disruptions__form" onSubmit={submitOpen} aria-label="建立異動案件">
          <h2>建立異動案件</h2>
          <label>原因<select value={form.reason} onChange={(event) => setForm({ ...form, reason: event.target.value })}>{REASON_OPTIONS.map((value) => <option key={value} value={value}>{reasonLabel(value)}</option>)}</select></label>
          <label>補充說明<textarea maxLength={500} value={form.reasonDetail} onChange={(event) => setForm({ ...form, reasonDetail: event.target.value })} /></label>
          <fieldset><legend>受影響訂位（至少一筆）</legend>{bookings.map((booking) => {
            const display = bookingPresentation(booking.bookingId, bookingIndex);
            return <label key={booking.bookingId}><input type="checkbox" checked={form.bookingIds.includes(String(booking.bookingId))} onChange={() => toggle('bookingIds', booking.bookingId)} />{display.primary}<span className="disruptions__secondary"> · {display.reference}</span></label>;
          })}</fieldset>
          <fieldset><legend>受影響旅伴</legend>{travelers.map((traveler) => {
            const display = travelerPresentation(traveler.travelerId, travelerIndex);
            return <label key={traveler.travelerId}><input type="checkbox" checked={form.affectedTravelerIds.includes(String(traveler.travelerId))} onChange={() => toggle('affectedTravelerIds', traveler.travelerId)} />{display.primary}<span className="disruptions__secondary"> · {display.reference}</span></label>;
          })}</fieldset>
          <div className="disruptions__actions"><button type="submit" disabled={busy || form.bookingIds.length === 0}>建立</button><button type="button" onClick={() => setShowOpen(false)}>取消</button></div>
        </form>
      ) : null}
    </main>
  );
}
